/**
 * Themed Login Component
 *
 * Shows the Amplify Authenticator with tenant branding
 * (logo, colors, background image)
 */

import React from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { useTenantBranding } from '../context/TenantContext';

export function ThemedLogin({ onBack }) {
  const branding = useTenantBranding();

  // Inline styles for dynamic theming
  // Use loginBackground color if provided, otherwise white for tenant portals, gray for default
  const bgColor = branding.colors?.loginBackground || '#ffffff';
  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bgColor,
    backgroundImage: branding.loginBackgroundUrl
      ? `url(${branding.loginBackgroundUrl})`
      : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  // Override Amplify UI colors with tenant brand colors
  const primaryColor = branding.colors?.primary || '#2563eb';
  const customCss = `
    [data-amplify-authenticator] {
      --amplify-components-button-primary-background-color: ${primaryColor};
      --amplify-components-button-primary-hover-background-color: ${primaryColor}dd;
      --amplify-components-button-link-color: ${primaryColor};
      --amplify-components-tabs-item-active-border-color: ${primaryColor};
      --amplify-components-tabs-item-active-color: ${primaryColor};
      --amplify-components-fieldcontrol-focus-border-color: ${primaryColor};
      --amplify-components-authenticator-router-box-shadow: none;
      --amplify-components-authenticator-router-border-width: 0;
      --amplify-components-authenticator-container-width-max: 100%;
    }
    [data-amplify-authenticator],
    [data-amplify-authenticator] [data-amplify-router],
    [data-amplify-authenticator] [data-amplify-container],
    [data-amplify-authenticator] form,
    [data-amplify-authenticator] .amplify-flex {
      max-width: 100%;
      box-sizing: border-box;
    }
    [data-amplify-authenticator] .amplify-input,
    [data-amplify-authenticator] input {
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }
  `;

  return (
    <div style={containerStyle}>
      {/* Inject custom CSS for Amplify UI */}
      <style>{customCss}</style>

      {/* Login card with backdrop */}
      <div className="w-full max-w-md px-4">
        {/* Logo / Company Name */}
        <div className="text-center mb-6">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.companyDisplayName}
              className="h-16 mx-auto mb-4"
            />
          ) : (
            <h1 className="text-2xl font-bold text-gray-800">
              {branding.companyDisplayName || 'Quote This Job'}
            </h1>
          )}
        </div>

        {/* Back button (only if not on a tenant subdomain) */}
        {onBack && (
          <button
            onClick={onBack}
            className="mb-4 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to home
          </button>
        )}

        {/* Amplify Authenticator */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <Authenticator
            hideSignUp={true}
            loginMechanisms={['email']}
          />
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-xs mt-6">
          Powered by Quote This Job
        </p>
      </div>
    </div>
  );
}

export default ThemedLogin;
